import { Box, Paper, Stack, Typography, CircularProgress } from "@mui/material";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";

export interface FilterBarProps {
  /** Filter controls (TextField, Button, etc.) */
  children: React.ReactNode;
  /** Show loading spinner at the end */
  loading?: boolean;
  /** Optional label above the row (e.g. "Filters") */
  label?: string;
}

/**
 * Consistent filter bar for list pages. Renders filters in a single row (wrapping on small screens)
 * with optional label and loading indicator. Aligns with backend filter params across Transfers,
 * Series, Analytics, Clients, Providers, Locations.
 */
export default function FilterBar({ children, loading = false, label = "Filters" }: FilterBarProps) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 2,
        bgcolor: "background.paper",
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      <Stack spacing={1.5} sx={{ minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <FilterListOutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
          <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
            {label}
          </Typography>
        </Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
          flexWrap="wrap"
          useFlexGap
          sx={{
            gap: 2,
            minWidth: 0,
            "& > *": { minWidth: 0, maxWidth: { xs: "100%", sm: "none" }, width: { xs: "100%", sm: "auto" } },
          }}
        >
          {children}
          {loading && (
            <Box sx={{ display: "flex", alignItems: "center", minHeight: 40 }}>
              <CircularProgress size={22} />
            </Box>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}
